import type { McLocation } from './types';

/**
 * Parse LiveAtlas/Dynmap location from URL.
 *
 * Hash form (canonical):  #world;flat;x,y,z;zoom
 * Path form (shared links): /world;flat;x,y,z;zoom
 * Query: ?x=&y=&z=&world=&zoom=&shot=
 *
 * Zoom is **Leaflet map zoom** (same as LiveAtlas currentZoom in the hash):
 *   0 = furthest out, higher = closer. Max ≈ mapzoomin + mapzoomout.
 * Do NOT invert against maxZoom — that made ;6 (classic sharp) become zoomed out
 * and ;3 load zz_ mid-res tiles (blurry).
 */
export function parseLocationFromUrl(): {
  location: McLocation | null;
  shotId: string | null;
} {
  const params = new URLSearchParams(window.location.search);
  const shotId = params.get('shot') || params.get('id');

  const hash = window.location.hash.replace(/^#/, '');
  const fromHash = parseDynmapLocationString(hash);
  if (fromHash) return { location: fromHash, shotId };

  // Path without leading slash, e.g. /world;flat;0,90,0;6
  const path = window.location.pathname.replace(/^\/+/, '');
  if (path && path.includes(';')) {
    const fromPath = parseDynmapLocationString(path);
    if (fromPath) return { location: fromPath, shotId };
  }

  const x = Number(params.get('x'));
  const y = Number(params.get('y'));
  const z = Number(params.get('z'));
  const world = params.get('world') || params.get('dimension') || 'world';
  if ([x, y, z].every(Number.isFinite)) {
    return {
      location: {
        world: normalizeWorldParam(world),
        mapName: params.get('map') || 'flat',
        x,
        y,
        z,
        zoom: params.get('zoom') != null ? Number(params.get('zoom')) : undefined,
      },
      shotId,
    };
  }

  return { location: null, shotId };
}

function parseDynmapLocationString(raw: string): McLocation | null {
  if (!raw || !raw.includes(';')) return null;
  const parts = raw.split(';');
  if (parts.length < 3) return null;
  const world = decodeURIComponent(parts[0] || 'world');
  const mapName = decodeURIComponent(parts[1] || 'flat');
  const xyz = (parts[2] || '').split(',').map(Number);
  const zoom = parts[3] != null && parts[3] !== '' ? Number(parts[3]) : undefined;
  if (xyz.length < 3 || !xyz.every(Number.isFinite)) return null;
  return {
    world: normalizeWorldParam(world),
    mapName,
    x: xyz[0],
    y: xyz[1],
    z: xyz[2],
    zoom: Number.isFinite(zoom) ? zoom : undefined,
  };
}

function normalizeWorldParam(raw: string): string {
  const k = raw.toLowerCase();
  if (k.includes('nether')) return 'world_nether';
  if (k.includes('end')) return 'world_the_end';
  if (k === 'overworld' || k === 'minecraft:overworld') return 'world';
  return raw || 'world';
}

export function writeLocationHash(loc: McLocation): void {
  const world = encodeURIComponent(loc.world);
  const mapName = encodeURIComponent(loc.mapName || 'flat');
  // LiveAtlas-compatible: store Leaflet zoom as-is (higher = closer)
  const zoom = loc.zoom != null && Number.isFinite(loc.zoom) ? Math.round(loc.zoom) : 0;
  const x = Math.round(loc.x);
  const y = Math.round(loc.y);
  const z = Math.round(loc.z);
  const next = `#${world};${mapName};${x},${y},${z};${zoom}`;
  if (window.location.hash !== next) {
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}${next}`);
  }
}

/** Open the same place on classic LiveAtlas (map.reborned.one). */
export function classicDynmapUrl(loc: McLocation): string {
  const world = encodeURIComponent(loc.world);
  const mapName = encodeURIComponent(loc.mapName || 'flat');
  const zoom =
    loc.zoom != null && Number.isFinite(loc.zoom) ? Math.round(loc.zoom) : 5;
  return `https://map.reborned.one/#${world};${mapName};${Math.round(loc.x)},${Math.round(loc.y)},${Math.round(loc.z)};${zoom}`;
}
